import {
  EndlinkMediaMap,
  SendCommunicationAttachmentResponse
} from "@coxauto-ui/cxm/communications/interfaces";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo
} from "react";
import type { MediaItem } from "@coxauto-ui/cxm/communications/interfaces";

export interface IEmailReplyContext {
  replyText: string;
  replyTextSubject: string;
  attachments: SendCommunicationAttachmentResponse[];
  selectedToChannelValue: string;
  selectedStationeryHTML: string | null;
  templateHead: string | null;
  selectedTemplateLoadState: {
    hasLoaded: boolean;
    loadedTemplateId: string | null;
  };
  ccRecipients: string[];
  bccRecipients: string[];
  selectedMedia: MediaItem[];
  endlinkMediaMap: EndlinkMediaMap;
  setReplyText: Dispatch<SetStateAction<string>>;
  setReplyTextSubject: Dispatch<SetStateAction<string>>;
  setAttachments: Dispatch<
    SetStateAction<SendCommunicationAttachmentResponse[]>
  >;
  setSelectedToChannelValue: Dispatch<SetStateAction<string>>;
  setSelectedStationeryHTML: Dispatch<SetStateAction<string | null>>;
  setTemplateHead: Dispatch<SetStateAction<string | null>>;
  setSelectedTemplateLoadState: Dispatch<
    SetStateAction<{
      hasLoaded: boolean;
      loadedTemplateId: string | null;
    }>
  >;
  setCcRecipients: Dispatch<SetStateAction<string[]>>;
  setBccRecipients: Dispatch<SetStateAction<string[]>>;
  setSelectedMedia: Dispatch<SetStateAction<MediaItem[]>>;
  setEndlinkMediaMap: Dispatch<SetStateAction<EndlinkMediaMap>>;
}

export const EmailReplyContext = createContext<IEmailReplyContext | undefined>(
  undefined
);

export interface EmailReplyProviderProps {
  value: IEmailReplyContext;
  children: ReactNode;
}

export function EmailReplyProvider({
  value,
  children
}: EmailReplyProviderProps) {
  const memoizedValue = useMemo(
    () => value,
    [
      value.replyText,
      value.replyTextSubject,
      value.attachments,
      value.selectedToChannelValue,
      value.selectedStationeryHTML,
      value.templateHead,
      value.selectedTemplateLoadState,
      value.ccRecipients,
      value.bccRecipients,
      value.selectedMedia,
      value.endlinkMediaMap,
      value.setReplyText,
      value.setReplyTextSubject,
      value.setAttachments,
      value.setSelectedToChannelValue,
      value.setSelectedStationeryHTML,
      value.setTemplateHead,
      value.setSelectedTemplateLoadState,
      value.setCcRecipients,
      value.setBccRecipients,
      value.setSelectedMedia,
      value.setEndlinkMediaMap
    ]
  );

  return (
    <EmailReplyContext.Provider value={memoizedValue}>
      {children}
    </EmailReplyContext.Provider>
  );
}

export function useEmailReply(): IEmailReplyContext {
  const context = useContext(EmailReplyContext);
  if (context === undefined) {
    throw new Error(
      "useEmailReply must be used within an EmailReplyProvider. " +
        "Make sure your component is wrapped with <EmailReplyProvider>."
    );
  }
  return context;
}

export default EmailReplyContext;
